import { FunctionComponent } from 'react';
import { AffectedEntityListView } from '../../shared/components/AffectedEntityListView/AffectedEntityListView';
import { ConfirmationBodyExtraParams } from '../../modal/ConfirmModal/ConfirmModal';
import { capitalise } from '../../shared/utils/string/formatter';
import { getPostConfirmationDeleteDialogProps } from '../../shared/components/PostConfirmationDeleteDialogProps/PostConfirmationDeleteDialogProps';
import { EntityActionConfirmDialogPropsValue } from '../interfaces/entity-actions';
import { GenericApiResponse } from '../../shared/interfaces/generics';

interface EntityDeleteActionConfirmDialogPropsFactoryProps {
  entity: string;
  affectedEntity: string;
  actionResolver: (id: number) => Promise<GenericApiResponse<unknown>>;
  affectedEntityResolver: (id: number) => Promise<GenericApiResponse<unknown>>;
  affectedEntityFactory: (rawEntity: unknown) => unknown;
  postConfirmationParams?: string;
  body?: string;
}

export function EntityDeleteActionConfirmDialogPropsFactory<E extends { id: number }>({
  entity,
  affectedEntity,
  actionResolver,
  affectedEntityFactory,
  affectedEntityResolver,
  postConfirmationParams,
  body
}: EntityDeleteActionConfirmDialogPropsFactoryProps): EntityActionConfirmDialogPropsValue<E> {
  const _entity = capitalise(entity);
  const _affectedEntity = capitalise(affectedEntity);

  return {
    title: `Delete ${_entity}?`,
    titleContainerClassName: 'text-red-40',
    positiveButtonText: 'Delete',
    negativeButtonText: 'Cancel',
    positiveButtonClass: 'btn-error',
    positiveButtonVariation: 'faded',
    positiveButtonPalette: 'error',
    positiveBtnProps: {
      icon: 'delete'
    },
    successMessage: `${_entity} Deleted`,
    body,
    actionHandler: args => actionResolver(args.entity.id),
    BodyComponent: AffectedEntityListView as FunctionComponent<
      object & Partial<ConfirmationBodyExtraParams<object>>
    >,
    bodyComponentProps: {
      entityActionResolver: (id: number) => affectedEntityResolver(id),
      entityFactory: (data: unknown) => affectedEntityFactory(data),
      affectedEntity: _affectedEntity,
      affectingEntity: _entity
    },
    confirmationMessage: `To confirm deletion, type <span class="text-bold">delete &lt;${entity.toLowerCase()}_name&gt;</span> in the field below and then click DELETE`,
    showConfirmationInput: true,
    confirmationValidation: (input, affectedEntityName) => {
      return input.trim() === `delete ${affectedEntityName.trim()}`;
    },
    postConfirmationDialogProps: getPostConfirmationDeleteDialogProps(
      _entity,
      postConfirmationParams
    )
  };
}
